// app/src/api/encounterHerbalApi.ts

import { createAsyncThunk } from "@reduxjs/toolkit"
import { serverApi, handleApiResponse, getAuthHeaders } from "@core/api/core"
import { EncounterHerbalWithItemSerializer, HerbalProcessStatus, EncounterHerbalTaListSerializer } from "@/types/encounterItem"
import { CommonResponse, CommonTResponse, commonPageResult } from "@/types/common"
import { AppDispatch } from "@core/store"
import { setHerbalPendingCount, setHerbalPendingLoading, setHerbalPendingError } from "@/features/hira/herbalPendingSlice"

// DEPRECATE(서버 전면 업데이트 후 제거):
// 구버전 서버( scaled 필드 미지원 )와의 하위호환을 위한 런타임 플래그
// 제거 시 아래 3개를 함께 삭제:
// 1) supportsChupScaledField
// 2) updateChupScaledSupport()
// 3) patchEncounterHerbal()의 requestData 분기
let supportsChupScaledField = false
let supportsHerbalSelection = false

export const supportsMultipleHerbalPrescriptions = () => supportsHerbalSelection

// DEPRECATE(서버 전면 업데이트 후 제거): GET 응답에서 scaled 필드 지원 여부를 학습
const updateChupScaledSupport = (payload: EncounterHerbalWithItemSerializer | EncounterHerbalWithItemSerializer[] | null | undefined) => {
  if (!payload) return
  const prescriptions = Array.isArray(payload) ? payload : [payload]
  if (prescriptions.some(prescription => prescription.encounterHerbalUuid || Object.prototype.hasOwnProperty.call(prescription, "linkType")))
    supportsHerbalSelection = true
  if (Array.isArray(payload)) {
    if (payload.length === 0) return
    const first = payload[0]
    if (first && Object.prototype.hasOwnProperty.call(first, "encounterHerbalChupCountScaled")) {
      supportsChupScaledField = true
    }
    return
  }
  if (Object.prototype.hasOwnProperty.call(payload, "encounterHerbalChupCountScaled")) {
    supportsChupScaledField = true
  }
}

export const patchEncounterHerbal = async (data: EncounterHerbalWithItemSerializer): Promise<boolean> => {
  const requestUrl = `/encounter/encounter_herbal/${data.encounterUuid}/`
  if (data.encounterHerbalUuid) supportsHerbalSelection = true
  if (!supportsHerbalSelection && data.linkType && data.encounterUuid) await getEncounterHerbalPrescriptions(data.encounterUuid)

  // DEPRECATE(서버 전면 업데이트 후 제거):
  // 미지원 서버에는 scaled 필드를 제외해서 PATCH 검증 오류를 방지
  // 서버 업데이트 완료 후: const requestData = data 로 단순화
  const requestData = { ...data }
  if (!supportsChupScaledField) delete requestData.encounterHerbalChupCountScaled
  if (!supportsHerbalSelection) {
    delete requestData.encounterHerbalUuid
    delete requestData.linkType
  }
  // console.log("patchEncounterHerbal data:", requestData)
  const response = await serverApi.patch<CommonResponse>(requestUrl, requestData)
  return handleApiResponse<boolean>(response)
}

export const patchEncounterHerbalExtraData = async (
  encounterUuid: string,
  extraData: EncounterHerbalWithItemSerializer["extraData"],
  encounterHerbalUuid?: string
): Promise<boolean> => {
  const requestUrl = `/encounter/encounter_herbal/${encounterUuid}/`
  const response = await serverApi.patch<CommonResponse>(requestUrl, {
    extraData,
    ...(supportsHerbalSelection && encounterHerbalUuid ? { encounterHerbalUuid } : {}),
  })
  return handleApiResponse<boolean>(response)
}

export const getEncounterHerbal = async (encounterUuid: string, herbalUuid?: string): Promise<EncounterHerbalWithItemSerializer> => {
  const requestUrl = `/encounter/encounter_herbal/${encounterUuid}/`
  const response = await serverApi.get<CommonResponse>(requestUrl, { params: herbalUuid ? { herbal_uuid: herbalUuid } : undefined })
  // console.log("getEncounterHerbal response:", response)
  const result = handleApiResponse<EncounterHerbalWithItemSerializer>(response)
  updateChupScaledSupport(result)
  return result
}

export const getEncounterHerbalEditable = async (encounterUuid: string, herbalUuid?: string): Promise<EncounterHerbalWithItemSerializer | null> => {
  const requestUrl = `/encounter/encounter_herbal/${encounterUuid}/editable/`
  const response = await serverApi.get<CommonResponse>(requestUrl, {
    params: herbalUuid ? { herbal_uuid: herbalUuid } : undefined,
    validateStatus: (status: number) => (status >= 200 && status < 300) || status === 404,
  })

  if (response.status === 404) {
    return null
  }

  const result = handleApiResponse<EncounterHerbalWithItemSerializer>(response)
  updateChupScaledSupport(result)
  return result
}

export const getPatientHerbalHistory = async (patientUuid: string): Promise<EncounterHerbalWithItemSerializer[]> => {
  const requestUrl = `/encounter/encounter_herbal/history/`
  const response = await serverApi.get(requestUrl, {
    params: { patient_uuid: patientUuid },
  })
  const result = handleApiResponse<EncounterHerbalWithItemSerializer[]>(response)
  updateChupScaledSupport(result)
  return result
}

export const getPatientHerbalHistoryEditable = async (patientUuid: string): Promise<EncounterHerbalWithItemSerializer[]> => {
  const requestUrl = `/encounter/encounter_herbal/history-editable/`
  const response = await serverApi.get(requestUrl, {
    params: { patient_uuid: patientUuid },
  })
  const result = handleApiResponse<EncounterHerbalWithItemSerializer[]>(response)
  updateChupScaledSupport(result)
  return result
}

export const getEncounterHerbalPrescriptions = async (encounterUuid: string): Promise<EncounterHerbalWithItemSerializer[] | null> => {
  const response = await serverApi.get(`/encounter/encounter_herbal/${encounterUuid}/prescriptions/`, {
    params: { editable: true },
    validateStatus: (status: number) => (status >= 200 && status < 300) || status === 404,
  })
  if (response.status === 404) {
    supportsHerbalSelection = false
    return null
  }
  supportsHerbalSelection = true
  const result = handleApiResponse<EncounterHerbalWithItemSerializer[]>(response)
  updateChupScaledSupport(result)
  return result
}

export const createAdditionalEncounterHerbal = async (
  encounterUuid: string,
  data: Partial<EncounterHerbalWithItemSerializer> & { primaryLinkType: "general" | "nhis" | "jabo" }
): Promise<EncounterHerbalWithItemSerializer> => {
  const requestData = { ...data }
  if (!supportsChupScaledField) delete requestData.encounterHerbalChupCountScaled
  const response = await serverApi.post(`/encounter/encounter_herbal/${encounterUuid}/additional/`, requestData)
  return handleApiResponse<EncounterHerbalWithItemSerializer>(response)
}

export const getLinkedEncounterHerbal = async (
  encounterUuid: string,
  linkType: "nhis" | "jabo",
  herbalUuid?: string
): Promise<EncounterHerbalWithItemSerializer | null> => {
  if (herbalUuid) return getEncounterHerbalEditable(encounterUuid, herbalUuid)
  const prescriptions = await getEncounterHerbalPrescriptions(encounterUuid)
  if (prescriptions !== null) {
    return prescriptions.find(prescription => prescription.linkType === linkType) || (prescriptions[0]?.linkType == null ? prescriptions[0] : null)
  }
  return getEncounterHerbalEditable(encounterUuid)
}

export const deleteEncounterHerbal = async (encounterUuid: string, herbalUuid?: string): Promise<boolean> => {
  const requestUrl = `/encounter/encounter_herbal/${encounterUuid}/`
  const response = await serverApi.delete<CommonResponse>(requestUrl, { params: herbalUuid ? { herbal_uuid: herbalUuid } : undefined })
  return handleApiResponse<boolean>(response)
}

/**
 * 한약 처방 진행상태 업데이트
 * @param encounterUuid 진료 UUID
 * @param processStatus 변경할 진행상태
 * @returns 성공 여부
 */
export const updateHerbalProcessStatus = async (
  encounterUuid: string,
  processStatus: HerbalProcessStatus | string,
  encounterHerbalUuid?: string
): Promise<boolean> => {
  if (encounterHerbalUuid) supportsHerbalSelection = true
  const requestUrl = `/encounter/encounter_herbal/${encounterUuid}/`
  const response = await serverApi.patch<CommonResponse>(requestUrl, {
    encounter_herbal_process_status: processStatus,
    ...(supportsHerbalSelection && encounterHerbalUuid ? { encounterHerbalUuid } : {}),
  })
  handleApiResponse<void>(response)
  return true
}

/**
 * 자보 환자 한약 목록 조회 (페이지네이션 지원)
 * @param fromDate 시작일 (YYYY-MM-DD)
 * @param toDate 종료일 (YYYY-MM-DD)
 * @param patientUuid 환자 UUID (선택)
 * @returns 자보 환자의 EncounterHerbal 목록
 */
export const getTaHerbalList = async (
  fromDate: string,
  toDate: string,
  patientUuid?: string,
  insuranceUuid?: string
): Promise<EncounterHerbalTaListSerializer[]> => {
  const requestUrl = `/encounter/encounter_herbal/ta-list/`

  // params 동적 구성
  const params: any = {
    from_date: fromDate,
    to_date: toDate,
  }

  // patientUuid가 있으면 params에 추가
  if (patientUuid) {
    params.patient_uuid = patientUuid
  }

  if (insuranceUuid) {
    params.insurance_uuid = insuranceUuid
  }

  const response = await serverApi.get<CommonTResponse<EncounterHerbalTaListSerializer>>(requestUrl, {
    params,
  })
  return handleApiResponse<EncounterHerbalTaListSerializer[]>(response)
}

/**
 * 산재 환자 한약 목록 조회
 */
export const getSanjaeHerbalList = async (
  fromDate: string,
  toDate: string,
  patientUuid?: string,
  insuranceUuid?: string
): Promise<EncounterHerbalTaListSerializer[]> => {
  const params: Record<string, string> = {
    from_date: fromDate,
    to_date: toDate,
  }

  if (patientUuid) params.patient_uuid = patientUuid
  if (insuranceUuid) params.insurance_uuid = insuranceUuid

  const response = await serverApi.get<CommonTResponse<EncounterHerbalTaListSerializer>>("/encounter/encounter_herbal/sanjae-list/", {
    params,
    validateStatus: (status: number) => (status >= 200 && status < 300) || status === 404 || status === 405 || status === 501,
  })
  if (response.status === 404 || response.status === 405 || response.status === 501) return []

  const entries = handleApiResponse<unknown>(response)
  return Array.isArray(entries) ? (entries.filter(entry => entry !== null && typeof entry === "object") as EncounterHerbalTaListSerializer[]) : []
}

/**
 * 자보 환자 한약 목록 조회 (페이지네이션)
 * @param fromDate 시작일 (YYYY-MM-DD)
 * @param toDate 종료일 (YYYY-MM-DD)
 * @param page 페이지 번호
 * @param pageSize 페이지당 데이터 수 (기본값: 30)
 * @param patientUuid 환자 UUID (선택)
 * @returns { results, pagination }
 */
export const getTaHerbalListPaginated = async (
  fromDate: string,
  toDate: string,
  page: number = 1,
  pageSize: number = 30,
  patientUuid?: string,
  insuranceUuid?: string
): Promise<commonPageResult<EncounterHerbalTaListSerializer>> => {
  const requestUrl = `/encounter/encounter_herbal/ta-list/`

  // params 동적 구성
  const params: any = {
    from_date: fromDate,
    to_date: toDate,
    page,
    page_size: pageSize,
  }

  // patientUuid가 있으면 params에 추가
  if (patientUuid) {
    params.patient_uuid = patientUuid
  }

  if (insuranceUuid) {
    params.insurance_uuid = insuranceUuid
  }

  const response = await serverApi.get<CommonTResponse<EncounterHerbalTaListSerializer>>(requestUrl, {
    params,
  })

  // 페이지네이션 응답 처리
  const data = response.data
  return {
    results: data.results || [],
    pagination: data.pagination || {
      count: 0,
      currentPage: 1,
      totalPages: 1,
      pageSize: pageSize,
      hasNext: false,
      hasPrevious: false,
      nextPage: null,
      previousPage: null,
    },
  }
}

export const getTaHerbalsPendingCount = async (): Promise<number> => {
  const requestUrl = `/encounter/encounter_herbal/ta-pending-count/`

  const response = await serverApi.get<CommonResponse>(requestUrl)
  return handleApiResponse<number>(response)
}

/**
 * 심평원 미제출 자보 첩약 건수 조회 (Redux Thunk)
 */
export const fetchHerbalPendingCount = createAsyncThunk<
  number, // 성공 시 반환할 타입
  void, // 인자로 받을 객체 타입
  { rejectValue: string }
>("herbalPending/fetchCount", async (_, { rejectWithValue }) => {
  try {
    const count = await getTaHerbalsPendingCount()
    return count
  } catch (error: any) {
    return rejectWithValue(error.message || "자보한약 미제출 건수 조회 중 오류가 발생했습니다.")
  }
})
